import { useMemo } from 'react';
import { GridColumn } from 'src/components/GridWrapper';
import CustomFilterCell from 'src/components/custom-cells/CustomFilterCell';
import DetailColumnCell from 'src/components/custom-cells/DetailCell';
import DetailHeaderCell from 'src/components/custom-cells/DetailHeaderCell';
import {
  BlindReceivePermissions,
  ThreeWayMatchingPermissions,
} from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { InvoiceContext } from 'src/sections/invoice_details/InvoiceDetailsContext';
import DescriptionTextField from 'src/sections/invoice_details/lineItemsColumns/DescriptionTextField';
import ErrorFilterMenuCell from 'src/sections/invoice_details/lineItemsColumns/ErrorFilterMenuCell';
import GLCodeDropDownCell from 'src/sections/invoice_details/lineItemsColumns/GlCodeDropDown';
import InvoiceLineItemCompleted from 'src/sections/invoice_details/lineItemsColumns/InvoiceLineItemCompleted';
import IsTaxable from 'src/sections/invoice_details/lineItemsColumns/IsTaxable';
import ItemTextField from 'src/sections/invoice_details/lineItemsColumns/ItemTextField';
import LineItemInfo from 'src/sections/invoice_details/lineItemsColumns/LineItemInfo';
import PoPercentage from 'src/sections/invoice_details/lineItemsColumns/PoPercent';
import PriceTextField from 'src/sections/invoice_details/lineItemsColumns/PriceTextField';
import QuantityReceived from 'src/sections/invoice_details/lineItemsColumns/QuantityReceived';
import ReceiptPercentage from 'src/sections/invoice_details/lineItemsColumns/ReceiptPercent';
import SubTotalField from 'src/sections/invoice_details/lineItemsColumns/SubTotalField';
import UOMTextField from 'src/sections/invoice_details/lineItemsColumns/UOMTextField';
import { InvoiceLineItem } from 'src/services/useInvoiceLineItemsApi';
import { useContextSelector } from 'use-context-selector';

export const useInvoiceDetailsLineItems: () => GridColumn<InvoiceLineItem>[] = () => {
  const isThreeWayMatching: boolean = usePermission({ permissions: ThreeWayMatchingPermissions });
  const isInvoiceInProgress = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceInProgress
  );

  const columns: GridColumn<InvoiceLineItem>[] = useMemo(
    () => [
      {
        field: 'expanded',
        width: 45,
        component: DetailColumnCell,
        menu: false,
        headerCell: DetailHeaderCell,
        sortable: false,
      },
      ...(!isInvoiceInProgress
        ? []
        : [
            {
              field: 'selected',
              title: '',
              width: 30,
              format: '',
              menu: false,
            },
          ]),
      {
        field: 'errors',
        title: 'Line Item Warnings',
        width: 45,
        component: LineItemInfo,
        menu: !!isInvoiceInProgress,
        filter: 'boolean',
        columnMenu: ErrorFilterMenuCell,
      },
      {
        field: 'completed',
        title: 'All Items Invoiced',
        width: 80,
        component: InvoiceLineItemCompleted,
        menu: false,
        sortable: false,
      },
      {
        field: 'isTaxable',
        title: 'Taxable',
        filter: 'boolean',
        menu: !!isInvoiceInProgress,
        component: IsTaxable,
        width: 80,
      },
      {
        field: 'quantityReceived',
        title: 'Current Doc Qty',
        filter: 'numeric',
        width: 120,
        menu: !!isInvoiceInProgress,
        component: QuantityReceived,
      },
      {
        field: 'previousQtys',
        title: '% vs PO',
        filter: 'numeric',
        width: 100,
        component: PoPercentage,
        format: '{0:p}',
        columnMenu: CustomFilterCell,
        filterFormat: 'p2',
        step: 0.01,
        menu: !!isInvoiceInProgress,
      },
      ...(isThreeWayMatching
        ? ([
            {
              field: 'poQty',
              title: '% vs Receipt',
              filter: 'numeric',
              width: 100,
              component: ReceiptPercentage,
              format: '{0:c2}%',
              filterFormat: 'p2',
              menu: !!isInvoiceInProgress,
              step: 0.01,
            },
          ] as const)
        : []),
      {
        field: 'productItemNum',
        title: 'Item #',
        filter: 'text',
        menu: !!isInvoiceInProgress,
        component: ItemTextField,
        width: 80,
      },
      {
        field: 'description',
        title: 'Description',
        filter: 'text',
        menu: !!isInvoiceInProgress,
        width: 250,
        component: DescriptionTextField,
      },
      {
        field: 'glCodeDescription',
        title: 'GL Code',
        filter: 'text',
        menu: !!isInvoiceInProgress,
        width: 250,
        component: GLCodeDropDownCell,
      },
      {
        field: 'uom',
        title: 'UOM',
        filter: 'text',
        menu: !!isInvoiceInProgress,
        component: UOMTextField,
        width: 100,
      },
      {
        field: 'priceAmt',
        title: 'Price',
        filter: 'numeric',
        menu: !!isInvoiceInProgress,
        format: '{0:c2}',
        component: PriceTextField,
        width: 100,
        filterFormat: 'c2',
      },
      {
        field: 'subTotal',
        title: 'Sub Total',
        filter: 'numeric',
        format: '{0:c2}',
        menu: !!isInvoiceInProgress,
        component: SubTotalField,
        width: 100,
        filterFormat: 'c2',
      },
    ],
    [isInvoiceInProgress, isThreeWayMatching]
  );

  return columns;
};

export const useInvoiceDetailsLineItemsSubGrid = () => {
  const isThreeWayMatching: boolean = usePermission({ permissions: ThreeWayMatchingPermissions });
  const isBlindReceive = usePermission({ permissions: BlindReceivePermissions });
  const isInvoiceInProgress = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceInProgress
  );
  const columns: GridColumn<any>[] = useMemo(
    () => [
      {
        field: 'previousQty',
        title: 'Prev Docs',
        width: 130,
        menu: !!isInvoiceInProgress,
      },
      ...(isBlindReceive
        ? []
        : ([
            {
              field: 'poQty',
              title: 'PO Qty',
              width: 130,
              menu: !!isInvoiceInProgress,
            },
          ] as GridColumn<any>[])),

      ...(isThreeWayMatching
        ? ([
            {
              field: 'receivedQty',
              title: 'Rec Qty',
              width: 130,
              menu: !!isInvoiceInProgress,
            },
          ] as GridColumn<any>[])
        : []),
      {
        field: 'poGlCodeDescription',
        title: 'PO GL Code',
        width: 350,
        menu: !!isInvoiceInProgress,
      },
      {
        field: 'poUom',
        title: 'PO UOM',
        width: 130,
        menu: !!isInvoiceInProgress,
      },
      {
        field: 'poPrice',
        title: 'PO Price',
        width: 130,
        format: '{0:c}',
        dataType: 'dollar',
        menu: !!isInvoiceInProgress,
      },
      {
        field: 'poSubtotal',
        title: 'PO Subtotal',
        width: 130,
        menu: !!isInvoiceInProgress,
        format: '{0:c}',
        dataType: 'dollar',
      },
    ],
    [isBlindReceive, isInvoiceInProgress, isThreeWayMatching]
  );

  return columns;
};
